import { T } from '@tolgee/react';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useState } from 'react';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import { useMetrics } from '../../../hooks/useMetrics';
import { zIndexes } from '../../../utils/zIndexes';
import { Button } from '../../_atoms/Button';
import BaseModal from '../../_molecules/BaseModal';

interface EndOfLessonOverlayProps {
  visible: boolean;
  onViewReport: () => void;
  onContinueLesson?: () => void;
  isLoadingReport?: boolean;
  isPreview: boolean;
}

const EndOfLessonOverlay: React.FC<EndOfLessonOverlayProps> = ({
  visible,
  onViewReport,
  onContinueLesson,
  isPreview,
  isLoadingReport = false,
}) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const metrics = useMetrics();

  const handleViewReportClick = () => {
    metrics.logEvent('EndOfLesson.ViewReport.Click', { isPreview });
    setShowConfirmModal(true);
  };

  const handleConfirmEnd = () => {
    metrics.logEvent('EndOfLesson.ViewReport.Confirm', { isPreview });
    setShowConfirmModal(false);
    onViewReport();
  };

  const handleContinueLesson = () => {
    metrics.logEvent('EndOfLesson.BackToLesson.Click');
    onContinueLesson?.();
  };

  return (
    <AnimatePresence>
      {visible && (
        <OverlayContainer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <BlurBackground />
          <ContentContainer
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: 'spring',
              damping: 25,
              stiffness: 200,
              duration: 0.6,
            }}
          >
            <MainContent>
              <Subtitle>
                <T
                  keyName="You've reached the end of the lesson."
                  defaultValue="You've reached the end of the lesson."
                />
              </Subtitle>
              <ButtonContainer>
                <ViewReportButton
                  type="primary"
                  loading={isLoadingReport}
                  onClick={handleViewReportClick}
                  styles={{
                    backgroundColor: theme.colors.orange,
                    border: 'none',
                    color: theme.colors.white,
                    fontSize: '24px',
                    padding: '20px 40px',
                    height: 'auto',
                    borderRadius: '10px',
                    fontWeight: 'bold',
                  }}
                >
                  {isPreview ? (
                    <T
                      keyName="Close classroom simulator"
                      defaultValue="Close classroom simulator"
                    />
                  ) : (
                    <T keyName="View report" defaultValue="View report" />
                  )}
                </ViewReportButton>
                <ContinueLessonButton
                  type="primary"
                  onClick={handleContinueLesson}
                  styles={{
                    color: theme.colors.white,
                    fontSize: '24px',
                    padding: '20px 40px',
                    height: 'auto',
                    borderRadius: '10px',
                    fontWeight: 'bold',
                  }}
                >
                  <T keyName="Back to lesson" defaultValue="Back to lesson" />
                </ContinueLessonButton>
              </ButtonContainer>
            </MainContent>
          </ContentContainer>

          {/* Confirmation Modal */}
          <BaseModal
            width="600px"
            centered
            visible={showConfirmModal}
            title={
              <h1
                style={{
                  fontSize: '36px',
                  textAlign: 'left',
                  fontWeight: 'bold',
                  marginBottom: 0,
                }}
              >
                <T keyName="End lesson confirmation" defaultValue="Are you sure?" />
              </h1>
            }
          >
            <p style={{ marginBottom: 20, fontSize: '20px', fontWeight: 'bold' }}>
              {isPreview ? null : (
                <T
                  keyName="End lesson confirmation description"
                  defaultValue="Viewing the report will end the lesson for your students."
                />
              )}
            </p>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 10,
              }}
            >
              <Button
                type="ghost"
                styles={{
                  border: 'none',
                  boxShadow: 'none',
                }}
                onClick={() => {
                  metrics.logEvent('EndOfLesson.ConfirmModal.Cancel');
                  setShowConfirmModal(false);
                }}
              >
                <T keyName="Cancel" defaultValue="Cancel" />
              </Button>

              <Button
                type="orange"
                styles={{
                  border: 'none',
                  color: theme.colors.white,
                  boxShadow: 'none',
                }}
                loading={isLoadingReport}
                onClick={handleConfirmEnd}
              >
                {isLoadingReport ? (
                  <T keyName="Loading report" defaultValue="Loading report" />
                ) : isPreview ? (
                  <T
                    keyName="Close classroom simulator"
                    defaultValue="Close classroom simulator"
                  />
                ) : (
                  <T keyName="View report" defaultValue="View report" />
                )}
              </Button>
            </div>
          </BaseModal>
        </OverlayContainer>
      )}
    </AnimatePresence>
  );
};

const OverlayContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: ${zIndexes('AI_FEEDBACK_OVERLAY')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BlurBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${theme.colors.blackPure};
`;

const ContentContainer = styled(motion.div)`
  position: relative;
  z-index: ${zIndexes('AI_FEEDBACK_OVERLAY') + 1};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 30px;
`;

const _Title = styled.h1`
  font-size: 80px;
  font-weight: bold;
  color: ${theme.colors.white};
  text-align: center;
  margin: 0;
  line-height: 1.2;

  @media (max-width: 992px) {
    font-size: 50px;
  }
`;

const Subtitle = styled.p`
  font-size: 62px;
  color: ${theme.colors.white};
  text-align: center;
  margin: 0;
  opacity: 0.9;

  @media (max-width: 992px) {
    font-size: 24px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;
`;

const ViewReportButton = styled(Button)`
  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.orangeDark} !important;
  }
`;

const ContinueLessonButton = styled(Button)`
  background-color: ${theme.colors.greyDark};
  border: 1px solid ${theme.colors.greyLight};
  &:hover,
  &:focus,
  &:active {
    color: ${theme.colors.black} !important;
    background-color: ${theme.colors.greyLight} !important;
  }
`;

export default EndOfLessonOverlay;
